import { create } from 'zustand'

import { AuthCookieService } from '@utils/AuthCookieService'
import { syncSentryUser } from '@libs/sentry/sentry'

export type AuthStatus = 'loading' | 'success' | 'failed' | undefined

export interface AuthState {
  accessToken?: string
  refreshToken?: string
  isInitialized: boolean
  status: {
    signIn?: AuthStatus
    signUp?: AuthStatus
  }
}

export type SignInSuccessType = {
  accessToken: string
  refreshToken: string
}

export type SignUpSuccessType = {
  accessToken: string
  refreshToken: string
}

interface AuthStore {
  state: AuthState
  actions: {
    initializeAuthState: (payload: { accessToken?: string }) => void
    signUpLoading: () => void
    signUpError: () => void
    signUpSuccess: (payload: SignUpSuccessType) => void
    signInLoading: () => void
    signInError: () => void
    signInSuccess: (payload: SignInSuccessType) => void
    signOut: () => void
  }
}

const initialState: AuthState = {
  accessToken: undefined,
  refreshToken: undefined,
  isInitialized: false,
  status: {
    signIn: undefined,
    signUp: undefined,
  },
}

export const useAuthStore = create<AuthStore>((set) => ({
  state: initialState,
  actions: {
    initializeAuthState: (payload) =>
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          isInitialized: true,
          status: {
            ...state.status,
            signIn: payload.accessToken ? 'success' : undefined,
          },
        },
      })),
    signUpLoading: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signUp: 'loading' } },
      })),
    signUpError: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signUp: 'failed' } },
      })),
    signUpSuccess: (payload) => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          status: { ...state.status, signUp: 'success', signIn: 'success' },
        },
      }))
      AuthCookieService.storeAuthInfo({ accessToken: payload.accessToken, refreshToken: payload.refreshToken }, true)
    },
    signInLoading: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signIn: 'loading' } },
      })),
    signInError: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signIn: 'failed' } },
      })),
    signInSuccess: (payload) => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          status: { ...state.status, signIn: 'success' },
        },
      }))
      AuthCookieService.storeAuthInfo({ accessToken: payload.accessToken, refreshToken: payload.refreshToken }, true)
    },
    signOut: () => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: undefined,
          refreshToken: undefined,
          status: { ...state.status, signIn: undefined, signUp: undefined },
        },
      }))
      AuthCookieService.clearAuthInfo()
      syncSentryUser(undefined)
    },
  },
}))

export const initializeAuth = async (): Promise<void> => {
  const { initializeAuthState } = useAuthStore.getState().actions
  try {
    const queryParams = new URLSearchParams(window.location.search)
    const accessToken = queryParams.get('accessToken')

    if (accessToken) {
      await AuthCookieService.storeAuthInfo({ accessToken }, true)
      initializeAuthState({ accessToken })
    } else {
      const authInfo = await AuthCookieService.getAuthInfo()
      initializeAuthState({ accessToken: authInfo?.accessToken })
    }
  } catch (err) {
    console.error('Error initializing auth state:', err)
    initializeAuthState({ accessToken: undefined })
  }
}
